import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styles from './ModernJobReview.module.css';
import { API_BASE_URL } from '../../config';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import Loader from '../../components/Loader';
import showToast from '../../components/ShowToast';
import { ArrowLeft, Star, Upload, X, Paperclip, Send } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../_newprofiles/components/ui/Card';
import { Button } from '../../_newprofiles/components/ui/Button';
import EmptyProfileImage from '../../images/navbar/empty-profile-image.png';

const ModernJobReview = ({ isPopup = false, jobId: propJobId, businessId: propBusinessId, jobDescription, businessName: propBusinessName, userName, reviewId, onClose }) => {
    const navigate = useNavigate();
    const params = useParams();
    const jobId = isPopup ? propJobId : params.jobId;
    const businessId = isPopup ? propBusinessId : params.businessId;

    const [rating, setRating] = useState(0);
    const [hover, setHover] = useState(0);
    const [reviewDescription, setReviewDescription] = useState('');
    const [files, setFiles] = useState([]);
    const [filePreviews, setFilePreviews] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loggedUserName, setLoggedUserName] = useState('');
    const [loggedUserProfilePic, setLoggedUserProfilePic] = useState(null);
    const [isViewMode, setIsViewMode] = useState(false); // New state for view mode
    const fileInputRef = useRef(null);

    const ratingCategories = [
        { id: 'quality', name: 'Quality' },
        { id: 'service', name: 'Service' },
        { id: 'communication', name: 'Communication' },
        { id: 'promptness', name: 'Promptness' },
        { id: 'completion', name: 'Completion' },
    ];

    const [categoryRatings, setCategoryRatings] = useState(
        ratingCategories.reduce((acc, category) => ({ ...acc, [category.id]: 0 }), {})
    );
    const [categoryHover, setCategoryHover] = useState(
        ratingCategories.reduce((acc, category) => ({ ...acc, [category.id]: 0 }), {})
    );

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            const decodedToken = jwtDecode(token);
            setLoggedUserName(decodedToken.UserName || 'User');
            setLoggedUserProfilePic(sessionStorage.getItem('userImage'));
        }

        if (reviewId) {
            setIsViewMode(true);
            const fetchReviewDetails = async () => {
                setLoading(true);
                try {
                    const response = await axios.get(`${API_BASE_URL}/api/BusinessReview/getReviewById?reviewId=${reviewId}`);
                    const reviewData = response.data.model; // Assuming the review data is in response.data.model
                    if (reviewData) {
                        setRating(reviewData.overallRating || 0);
                        setReviewDescription(reviewData.description || '');
                        setCategoryRatings({
                            quality: reviewData.ratingQuality || 0,
                            service: reviewData.ratingService || 0,
                            communication: reviewData.ratingCommunication || 0,
                            promptness: reviewData.ratingPromptness || 0,
                            completion: reviewData.ratingCompletion || 0,
                        });
                        // Using the correct property name 'mediaUrl' from ViewReviewForEditResponseDto
                        if (reviewData.mediaUrl && Array.isArray(reviewData.mediaUrl)) {
                            setFilePreviews(reviewData.mediaUrl.map(file => file.url));
                        }
                    }
                } catch (error) {
                    console.error('Error fetching review details:', error);
                    showToast('error', 'Failed to load review details.');
                } finally {
                    setLoading(false);
                }
            };
            fetchReviewDetails();
        } else {
            setIsViewMode(false);
        }
    }, [reviewId]); // Depend on reviewId to re-fetch if it changes

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        setFiles((prevFiles) => [...prevFiles, ...selectedFiles]);

        const previews = selectedFiles.map((file) => URL.createObjectURL(file));
        setFilePreviews((prevPreviews) => [...prevPreviews, ...previews]);
    };

    const handleRemoveFile = (index) => {
        const newFiles = files.filter((_, i) => i !== index);
        setFiles(newFiles);

        const newFilePreviews = filePreviews.filter((_, i) => i !== index);
        setFilePreviews(newFilePreviews);
        URL.revokeObjectURL(filePreviews[index]); // Clean up object URL
    };

    const handleCategoryRating = (category, value) => {
        setCategoryRatings((prev) => ({ ...prev, [category]: value }));
    };

    const handleCategoryHover = (category, value) => {
        setCategoryHover((prev) => ({ ...prev, [category]: value }));
    };


    const handleSubmitReview = async () => { // Removed isPublish parameter as it's always true now

        setLoading(true);
        const token = localStorage.getItem('token');

        if (!token) {
            showToast('error', 'You must be logged in to submit a review.');
            setLoading(false);
            return;
        }

        const userId = token ? jwtDecode(token).nameid : null;

        if (!userId) {
            showToast('error', 'You must be logged in to submit a review.');
            setLoading(false);
            return;
        }

        //GET THE USERTYPE

        const decodedToken = jwtDecode(token);
        const userTypeOG = decodedToken.UserType;
        var userType = '';
        
        if (userTypeOG === 'Home') {
            userType = "HomeUser";
        } else {
            userType = "BusinessUser";
        }


        const formData = new FormData();
        formData.append('businessId', businessId);
        formData.append('jobId', jobId);
        formData.append('userId', userId);

        formData.append('userType', userType);
        
        formData.append('businessName', propBusinessName); // Add businessName to formData
        formData.append('description', reviewDescription);
        formData.append('overallRating', rating);
        formData.append('ratingQuality', categoryRatings.quality);
        formData.append('ratingService', categoryRatings.service);
        formData.append('ratingCommunication', categoryRatings.communication);
        formData.append('ratingPromptness', categoryRatings.promptness);
        formData.append('ratingCompletion', categoryRatings.completion);

        files.forEach((file) => {
            formData.append('files', file);
        });

        const endpoint = `${API_BASE_URL}/api/BusinessReview/save`; // Always publish

        try {
            const response = await axios.post(endpoint, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            if (response.status === 200) {
                showToast('success', `Review published successfully!`);
                if (isPopup) {
                    onClose();
                } else {
                    navigate(`/job-detail-new/${jobId}`); // Redirect back to the job detail page
                }
            }
        } catch (error) {
            console.error('Error submitting review:', error);
            showToast('error', 'Failed to submit review. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={isPopup ? styles.popupContainer : styles.pageContainer}>
            {loading && <Loader />}
            {!isPopup && (
                <header className={styles.header}>
                    <div className={styles.headerContent}>
                        <Button variant="ghost" onClick={() => navigate(-1)}>
                            <ArrowLeft className={styles.icon} />
                            Back
                        </Button>
                        <h1 className={styles.headerTitle}>Write a Review</h1>
                    </div>
                </header>
            )}

            <main className={isPopup ? styles.popupMainContent : styles.mainContent}>
                <Card className={styles.reviewCard}>
                    <CardHeader>
                        <CardTitle className={styles.cardTitle}>
                            Reviewing: {propBusinessName}
                        </CardTitle>
                        <p className={styles.jobDetailsSubtitle}>
                            for Job Name: {jobDescription} ({jobId})
                        </p>
                        <div className={styles.reviewerInfo}>
                            <img
                                src={loggedUserProfilePic === "null" ? EmptyProfileImage : loggedUserProfilePic}
                                alt={loggedUserName}
                                className={styles.avatar}
                            />
                            <span>{loggedUserName}</span>
                        </div>
                    </CardHeader>
                    <CardContent className={styles.cardContent}>
                        <div className={styles.section}>
                            <h3 className={styles.sectionTitle}>Overall Rating</h3>
                            <div className={styles.starRating}>
                                {[...Array(5)].map((_, index) => {
                                    const starValue = index + 1;
                                    return (
                                    <Star
                                        key={starValue}
                                        className={`${styles.starIcon} ${starValue <= (hover || rating) ? styles.starFilled : styles.starEmpty}`}
                                        onMouseEnter={() => !isViewMode && setHover(starValue)}
                                        onMouseLeave={() => !isViewMode && setHover(0)}
                                        onClick={() => !isViewMode && setRating(starValue)}
                                    />
                                    );
                                })}
                            </div>
                        </div>

                        <div className={styles.section}>
                            <h3 className={styles.sectionTitle}>Detailed Ratings</h3>
                            <div className={styles.categoryRatingsGrid}>
                                {ratingCategories.map((category) => (
                                    <div key={category.id} className={styles.categoryRating}>
                                        <label className={styles.categoryLabel}>{category.name}</label>
                                        <div className={styles.starRating}>
                                            {[...Array(5)].map((_, index) => {
                                                const starValue = index + 1;
                                                return (
                                                    <Star
                                                        key={starValue}
                                                        className={`${styles.starIconSmall} ${starValue <= (categoryHover[category.id] || categoryRatings[category.id]) ? styles.starFilled : styles.starEmpty}`}
                                                        onMouseEnter={() => !isViewMode && handleCategoryHover(category.id, starValue)}
                                                        onMouseLeave={() => !isViewMode && handleCategoryHover(category.id, 0)}
                                                        onClick={() => !isViewMode && handleCategoryRating(category.id, starValue)}
                                                    />
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className={styles.section}>
                            <h3 className={styles.sectionTitle}>Your Review</h3>
                            <textarea
                                placeholder="Share your experience with this business..."
                                className={styles.textarea}
                                value={reviewDescription}
                                onChange={(e) => setReviewDescription(e.target.value)}
                                readOnly={isViewMode} // Disable in view mode
                            />
                        </div>

                        <div className={styles.section}>
                            <h3 className={styles.sectionTitle}>Attached Photos</h3>
                            {!isViewMode && (
                                <div className={styles.uploadArea}>
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        multiple
                                        accept="image/*"
                                        className={styles.fileInput}
                                        onChange={handleFileChange}
                                        id="file-upload"
                                        disabled={isViewMode} // Disable in view mode
                                    />
                                    <label htmlFor="file-upload" className={styles.uploadLabel}>
                                        <Upload className={styles.icon} />
                                        <span>Click to upload or drag and drop</span>
                                        <span className={styles.uploadHint}>PNG, JPG, GIF up to 10MB</span>
                                    </label>
                                </div>
                            )}
                            {filePreviews.length > 0 && (
                                <div className={styles.filePreviewGrid}>
                                    {filePreviews.map((preview, index) => (
                                        <div key={index} className={styles.filePreviewItem}>
                                            <img src={preview} alt={`preview-${index}`} className={styles.previewImage} />
                                            {!isViewMode && ( // Only show remove button in edit mode
                                                <button onClick={() => handleRemoveFile(index)} className={styles.removeFileButton}>
                                                    <X size={16} />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {!isViewMode && ( // Only show publish button in edit mode
                            <div className={styles.actionButtons}>
                                <Button onClick={handleSubmitReview} className={styles.publishButton}>
                                    <Send className={styles.icon} />
                                    Publish
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </main>
        </div>
    );
};

export default ModernJobReview;
